import { NoticeFlow, NoticeStep } from "../types/notice";

/** Opens the wizard when a Template has Blanks (desktop only). */
export const FILL_IN_THE_BLANKS_STEP: NoticeStep = {
  id: "blanks",
  component: "FillInTheBlanksStep",
  isRequired: false,
};

export const noticeFlows: Record<string, NoticeFlow> = {
  info: {
    id: "info",
    name: "Send a Notice",
    description: "Share information with the entire circle",
    type: "info",
    steps: [
      { id: "title", component: "TitleStep", isRequired: true },
      { id: "content", component: "ContentStep", isRequired: true },
      { id: "eventPublish", component: "EventPublishStep", isRequired: true },
      { id: "review", component: "ReviewStep", isRequired: true },
    ],
    defaultValues: {
      type: "info",
      audience: "",
      attachmentUrl: "",
      eventBackgroundImage: "",
    },
  },
  direct: {
    id: "direct",
    name: "Direct notice",
    description: "Send a private notice to one person in your circle",
    type: "direct",
    steps: [
      { id: "title", component: "TitleStep", isRequired: true },
      { id: "content", component: "ContentStep", isRequired: true },
      { id: "review", component: "ReviewStep", isRequired: true },
    ],
    defaultValues: {
      type: "direct",
      audience: "circle",
      attachmentUrl: "",
      eventBackgroundImage: "",
    },
  },
  urgent: {
    id: "urgent",
    name: "Urgent Notice",
    description: "Send critical alerts to the entire circle",
    type: "urgent",
    steps: [
      { id: "title", component: "TitleStep", isRequired: true },
      { id: "content", component: "ContentStep", isRequired: true },
      { id: "eventPublish", component: "EventPublishStep", isRequired: true },
      { id: "review", component: "ReviewStep", isRequired: true },
    ],
    defaultValues: {
      type: "urgent",
      audience: "circle",
      attachmentUrl: "",
      eventBackgroundImage: "",
    },
  },
  image: {
    id: "image",
    name: "Attachment",
    description: "Share a notice with a title and an attachment.",
    type: "image",
    steps: [
      { id: "Details", component: "ImageTitleStep", isRequired: true },
      { id: "eventPublish", component: "EventPublishStep", isRequired: true },
      { id: "review", component: "ReviewStep", isRequired: true },
    ],
    defaultValues: {
      type: "image",
      audience: "",
      attachmentUrl: "",
      eventBackgroundImage: "",
    },
  },
  image_notice: {
    id: "image_notice",
    name: "Image",
    description: "Share a notice with a title and an image.",
    type: "image",
    steps: [
      { id: "title", component: "ImageOnlyTitleStep", isRequired: true },
      { id: "eventPublish", component: "EventPublishStep", isRequired: true },
      { id: "review", component: "ReviewStep", isRequired: true },
    ],
    defaultValues: {
      type: "image",
      audience: "",
      attachmentUrl: "",
      eventBackgroundImage: "",
    },
  },
  poll: {
    id: "poll",
    name: "Poll",
    description: "Ask a question with up to 10 answer choices.",
    type: "poll",
    steps: [
      { id: "poll", component: "PollStep", isRequired: true },
      { id: "review", component: "ReviewStep", isRequired: true },
    ],
    defaultValues: {
      type: "poll",
      audience: "",
      attachmentUrl: "",
      eventBackgroundImage: "",
    },
  },
  claims: {
    id: "claims",
    name: "Claims",
    description:
      "Create a sign-up list where each option can only be claimed once.",
    type: "poll",
    steps: [
      { id: "poll", component: "PollStep", isRequired: true },
      { id: "review", component: "ReviewStep", isRequired: true },
    ],
    defaultValues: {
      type: "poll",
      audience: "",
      attachmentUrl: "",
      eventBackgroundImage: "",
    },
  },
  custom_event: {
    id: "custom_event",
    name: "Events",
    description: "Create an event notice with a custom branded background.",
    type: "custom_event",
    steps: [
      { id: "title", component: "TitleStep", isRequired: true },
      { id: "content", component: "ContentStep", isRequired: true },
      { id: "eventInfo", component: "EventInfoStep", isRequired: true },
      { id: "eventSettings", component: "EventSettingsStep", isRequired: true },
      { id: "review", component: "ReviewStep", isRequired: true },
    ],
    defaultValues: {
      type: "event",
      audience: "",
      attachmentUrl: "",
      eventStartDate: "",
      eventStartTime: "",
      eventEndDate: "",
      eventEndTime: "",
      eventBackgroundImage: "",
    },
  },
};

/** The step with this id as a 1-based step number; step 1 when the flow has no such step. */
export function stepNumberNamed(steps: NoticeStep[], name: string): number {
  const index = steps.findIndex((step) => step.id === name);
  return index === -1 ? 1 : index + 1;
}

export function flowHasSchedulingStep(flowId: string): boolean {
  return (
    noticeFlows[flowId]?.steps.some(
      (step) => step.component === "EventPublishStep",
    ) ?? false
  );
}

/** True when publish timing is collected before review (scheduling or event settings). */
export function flowHasPublishConfiguredBeforeReview(flowId: string): boolean {
  return (
    noticeFlows[flowId]?.steps.some(
      (step) =>
        step.component === "EventPublishStep" ||
        step.component === "EventSettingsStep",
    ) ?? false
  );
}
